/**
 * The one pinyin letter the site's faces cannot draw, written another way.
 *
 * `ḿ` is the syllabic nasal of 呣, and of 呒 and 嘸 in their dialectal reading
 * of 没有. Unicode gives it a precomposed codepoint, U+1E3F, and no face this
 * site serves has a glyph for it. Nor does the Chinese fallback, so a page
 * writing it falls through the whole stack to whatever the visitor happens to
 * have installed.
 *
 * **Its tone-four partner has no such codepoint and has always drawn.** `m̀` is
 * `m` followed by U+0300, because Unicode never composed m-with-grave, and
 * every face carries both halves of that. So `/cidian/呣/` would set one of its
 * two readings in the site's face and the other in a stranger's.
 *
 * This writes `ḿ` the same way, as `m` followed by U+0301. The two forms are
 * canonically equivalent, so nothing that compares readings can tell them
 * apart: `foldPinyin` and the search index both normalise to NFD before they
 * fold, and a reader who types either one is asking the same question.
 *
 * **What it looks like was measured rather than assumed.** Literata carries
 * `ccmp` and `mark`, so it pulls the acute 478 units of its 1000-unit em back
 * over the letter and centres it. Baloo 2 has no mark anchors and cuts the
 * acute with a negative bounding box instead, which is how a face without them
 * is drawn to stack, and the mark lands over the letter's third stem rather
 * than its middle. Headings are Literata and body text is Baloo 2, so the
 * heading of an entry is right and the readings under it are close.
 *
 * **`@kensio/pinyinjs` is left alone.** U+1E3F is the correct spelling and the
 * package should keep writing it. What faces can draw is this site's problem.
 *
 * **Three pages use it**, out of a section of 41,003. Anything wider than one
 * codepoint would be answering a question nobody has asked.
 */

/**
 * The precomposed letters no served face draws, and how to write them instead.
 *
 * Escaped rather than written out, because the two sides of each pair are the
 * same letter and an editor showing them would show one string twice.
 */
const undrawable = new Map([
  ["\u1E3F", "m\u0301"],
  ["\u1E3E", "M\u0301"],
]);

/**
 * A pinyin spelling as the site's own faces can set it.
 *
 * Safe over markup as well as over text. The letter appears in neither a tag
 * name nor an attribute this site writes, so a reading already wrapped in its
 * tone-coloured spans can go through unchanged.
 * @param written - A reading, or anything holding one.
 * @returns The same reading, holding no letter the faces have no glyph for.
 */
export function drawablePinyin(written: string): string {
  return written.replaceAll(
    /[\u1E3E\u1E3F]/gu,
    (letter) => undrawable.get(letter) ?? letter,
  );
}
